import { ConversationType } from '../../../types/conversation.types';
import { UserRole } from '../../../types/user.types';

/**
 * Mentions, on both ends of their life: reading them out of a draft on the way
 * to the API, and finding them again in a body that is about to be rendered.
 *
 * Both directions match on the NAME rather than on anything stored with the
 * text. The body is a plain string the user keeps editing after picking a name,
 * so the only honest source of truth is what the text says right now: deleting
 * half a name has to un-mention that person, and typing one by hand has to
 * count. `mentionUserIds` rides along for the backend's benefit (a mention is
 * pushed even through a mute); it is not what decides what is drawn.
 */

/** Someone who can be mentioned - everyone in the conversation. */
export type MentionCandidate = {
  id: number;
  name: string;
  /**
   * Shown under the name in the `@` menu, and the only thing separating two
   * colleagues with the same first name. Optional because nothing else here
   * needs it: matching a handle in a body is done on the name alone.
   */
  role?: UserRole | null;
};

/**
 * Longest names first, so "Ana Maria" is never consumed as "Ana" with a stray
 * "Maria" left behind it. Both the resolver and the matcher depend on this:
 * regex alternation takes the first branch that fits, not the longest one.
 */
const byLongestName = (candidates: MentionCandidate[]) =>
  [...candidates].sort((a, b) => b.name.length - a.name.length);

/**
 * Which members the body actually names, for the send payload.
 */
export const resolveMentions = (
  body: string,
  candidates: MentionCandidate[]
) => {
  const ids: number[] = [];
  let remaining = body;

  byLongestName(candidates).forEach((candidate) => {
    const needle = `@${candidate.name}`;
    if (!remaining.includes(needle)) return;
    ids.push(candidate.id);
    remaining = remaining.split(needle).join(' ');
  });

  return ids;
};

/**
 * The handle that names the whole group, without the `@`.
 *
 * It lives in the text like any other handle, and the text is again what
 * decides: the backend never rewrites a body, so a `@all` deleted before
 * sending must not still notify forty people, and one typed by hand must.
 */
export const MENTION_ALL = 'all';

/** Only groups have an `@all`; support, direct and Viktor ignore the flag. */
export const canMentionAll = (type?: ConversationType | null) =>
  type === 'group' || type === 'merchantGroup';

/** Whether the body calls on the whole group, i.e. `mentionAll` on send. */
export const hasMentionAll = (body: string) =>
  new RegExp(`(^|\\s)@${MENTION_ALL}(?![\\p{L}\\p{N}])`, 'iu').test(body);

/** A body, cut into the plain stretches and the handles between them. */
export type MentionRun = {
  text: string;
  /** Set only on a handle: who it names. */
  userId?: number;
  /** Set only on `@all`, which names everyone and so nobody in particular. */
  all?: boolean;
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** `all` -> `[aA][lL][lL]`, for a case-insensitive piece of a sensitive regex. */
const anyCase = (word: string) =>
  [...word]
    .map((letter) => `[${letter.toLowerCase()}${letter.toUpperCase()}]`)
    .join('');

/**
 * The body, split so a handle can be drawn differently from the text around it.
 *
 * Only whole known names count. Highlighting every `@word` would light up email
 * addresses and anyone who left the group, and highlighting nothing when the
 * list is missing is the right failure - the message still reads exactly as it
 * was typed.
 *
 * The match needs a word boundary at both ends: an `@` mid-word is an address,
 * and without the trailing check "@Ana" would claim the first three letters of
 * "@Anastasija" and leave the rest as text.
 */
export const splitMentions = (
  body: string,
  candidates: MentionCandidate[] = [],
  /**
   * Picks out `@all` too - groups only. Its `mentionUserIds` list every member,
   * but what was written is `@all`, so that is all that is drawn.
   */
  withAll = false
): MentionRun[] => {
  if (!body) return [];
  if (!candidates.length && !withAll) return [{ text: body }];

  const ordered = byLongestName(candidates);
  const idsByName = new Map(ordered.map(({ name, id }) => [name, id]));

  /* `@all` is matched case-insensitively, the way the composer reads it on
     send, so it gets an alternation of its own: names stay case-sensitive.
     Spelled as letter classes because the inline `(?i:)` flag is too new for
     the Safari versions the tablets run. A member actually called "all" still
     wins, being first. */
  const names = ordered.map(({ name }) => escapeRegExp(name)).join('|');
  const pattern = new RegExp(
    `(^|\\s)@(?:(${names || '(?!)'})|(${
      withAll ? anyCase(MENTION_ALL) : '(?!)'
    }))(?![\\p{L}\\p{N}])`,
    'gu'
  );

  const runs: MentionRun[] = [];
  let cursor = 0;
  let match = pattern.exec(body);

  while (match !== null) {
    // Group 1 is the boundary before the handle and belongs to the text.
    const at = match.index + match[1].length;
    if (at > cursor) runs.push({ text: body.slice(cursor, at) });

    const name = match[2] ?? match[3];
    runs.push(
      match[2] !== undefined
        ? { text: `@${name}`, userId: idsByName.get(name) }
        : { text: `@${name}`, all: true }
    );

    cursor = at + name.length + 1;
    /* Resume immediately after the handle. Left alone, the next search would
       start where the whole match ended, which is the same place - but only
       because the boundary group is at the FRONT. Setting it explicitly keeps
       that from being a coincidence. */
    pattern.lastIndex = cursor;
    match = pattern.exec(body);
  }

  if (cursor < body.length) runs.push({ text: body.slice(cursor) });

  return runs;
};
